/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button } from "@mui/material";
import ContentShell from "components/ContentShell";
import { SimpleTextInput } from "components/FormTextInput";
import SimpleSwitch from "components/Switch";
import TranslationButtons from "components/TranslationButtons";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import {
  createPackCategory,
  getPackCategoryInfo,
  updatePackCategory,
} from "services/packCategoryServices";
import useStyles from "styles/store/storeEditor";

const PackCategoryEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { open, isOpen, close } = useOpenClose();
  const [categoryDetails, setCategoryDetails] = useState({
    name: "",
    nameTrans: {},
    order: null,
    isActive: true
  });
  const [modalData, setModalData] = useState({
    modal: "",
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const cancel = () => navigate(configData.routes.packCategory.packCategory);

  const submit = async () => {
    setLoading(true);
    try {
      let resp;
      if (id === "create") {
        resp = await createPackCategory({
          ...categoryDetails,
          order: categoryDetails.order ? Number(categoryDetails.order) : null,
          isActive: categoryDetails.isActive
        });
      } else {
        resp = await updatePackCategory({
          id: id,
          name: categoryDetails.name,
          nameTrans: categoryDetails.nameTrans,
          order: categoryDetails.order ? Number(categoryDetails.order) : null,
          isActive: categoryDetails.isActive
        });
      }
      showSnackbar(resp?.data?.message);
      setLoading(false);
      navigate(configData.routes.packCategory.packCategory);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(categoryDetails?.[field]).map(
      ([key, value]) => {
        return {
          lang: key,
          inputValue: value,
        };
      }
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error"
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setCategoryDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {}
      );
      return { ...prev };
    });
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "isActive") {
      setCategoryDetails((state) => ({ ...state, isActive: !state.isActive }));
      return;
    }
    setCategoryDetails((state) => ({ ...state, [name]: value }));
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setCategoryDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setCategoryDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const fetchPackCategoryInfo = async () => {
    try {
      const resp = await getPackCategoryInfo(id);
      setCategoryDetails((state) => {
        const _nameTrans = { ...state.nameTrans, ...resp.data?.nameTrans };
        if (resp?.data?.name && !_nameTrans["en"]) {
          _nameTrans["en"] = resp?.data?.name;
        } else resp.data.name = _nameTrans["en"];

        return {
          ...resp?.data,
          nameTrans: _nameTrans,
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    id !== "create" && fetchPackCategoryInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} Pack Category`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.formContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label={"Name"}
              name={"name"}
              onChange={onChange}
              value={categoryDetails.name}
              button={
                <TranslationButtons
                  field={"nameTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label={"Order"}
              name={"order"}
              onChange={onChange}
              value={categoryDetails.order}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSwitch
              legendText={"Status"}
              name="isActive"
              value={categoryDetails.isActive}
              label={categoryDetails.isActive ? "Active" : "Inactive"}
              onChange={onChange}
            />
          </Box>
        </Box>
      </Box>
      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        modalData={modalData}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell >
  );
};

export default PackCategoryEditor;
